RivalIQ.apps.Insights.module('widgets', function (widgets, App, Backbone, Marionette, $, _, d3) {

    'use strict';

    const stackedBarChartHeight = 200;
    const shortenedStackedBarChartHeight = 157;

    const deltaValueFormatter = function (value) {
        const prefix = (value > 0 ? '+' : '');
        return prefix + RivalIQ.formatter.formatAbbreviated(value);
    };

    const valueFormatter = function (value, metric, options) {
        if (_.get(options, ['expand'])) {
            return RivalIQ.formatter.formatExpandedNumeric(value);
        }

        if (_.get(options, ['value']) === 'deltaRelative') {
            return RivalIQ.formatter.formats.deltaRelative(value);
        }

        return RivalIQ.formatter.formatDecimal(value);
    };


    const Metrics = RivalIQ.metrics.descriptions;
    const widgetTypes = RivalIQ.enums.widgetTypes;

    const SectionLayout = RivalIQ.apps.Dashboard.views.Layout.mixin({
        childView: Marionette.Dashboard.views.Widgets,

        mixins: {
            normalizeHeights: RivalIQ.apps.Dashboard.mixins.NormalizeHeights
        },

        className: function () {
            const me = this;
            return me.dashboardModel.instanceId;
        },

        initialize: function () {
            const me = this;

            RivalIQ.apps.Dashboard.views.Layout.prototype.initialize.apply(me, arguments);
            me.collection = me.dashboardModel.getWidgets();
            me.listenTo(me.collection, 'reset update', me.refresh);
        },

        // Re-applies the current widget collection to the cached region collections so that
        // changes to the section's widget composition (e.g. the cutoff-driven prune in
        // pruneFacebookImpressionsReachWidgets) re-render in place. Backbone's `set` reconciles
        // by id, so sections whose composition hasn't changed are effectively no-ops. Kept
        // scoped to insights SectionLayout subclasses — the base Dashboard Layout.refresh
        // stays `_.noop` so non-insights layouts aren't pulled into this re-render path.
        refresh: function () {
            const me = this;

            if (!me.widgetRegions || !me.widgetRegions.cache) {
                return;
            }

            const widgetsByRegion = me.getCollection().groupBy(function (widget) {
                return widget.getRegion();
            });

            _.each(me.widgetRegions.cache, function (regionCollection, regionName) {
                const models = widgetsByRegion[regionName] || [];
                regionCollection.set(models);
            });
        }
    });

    const VerticalSection = SectionLayout.extend({
        template: '#insightsVerticalTemplate',

        sectionClassName: 'widgets-region panel-body',

        config: [
            [
                {
                    name: 'top',
                    sizes: {
                        sm: '12'
                    },
                    className: 'panel-list-group'
                },
                {
                    name: 'middle',
                    sizes: {
                        sm: '12'
                    },
                    className: 'panel-list-group'
                },
                {
                    name: 'bottom',
                    sizes: {
                        sm: '12'
                    },
                    className: 'panel-list-group'
                }
            ]
        ]
    });

    const VerticalWithFlexLayoutSection = SectionLayout.extend({
        template: '#insightsVerticalTemplate',

        sectionClassName: 'widgets-region panel-body',

        config: [
            [
                {
                    name: 'top',
                    sizes: {
                        sm: '12'
                    },
                    className: 'panel-list-group',

                    // This embeds a flex-region right after the widgets-region; for these layouts, the variable height piece is in the top widget, so we want
                    // to grow that region if the heights don't match.
                    templateFn: function () {
                        const widgetRegion = $.tagHtml('div', {
                            class: 'widgets-region panel-body',
                            html: $.tagHtml('div', {
                                class: 'panel-list-group'
                            })
                        });

                        const flexRegion = $.tagHtml('div', {
                            class: 'flex-region'
                        });

                        const html = $.tagHtml('div', {
                            class: 'insights-top-widget-region',
                            html: widgetRegion + flexRegion
                        });

                        return _.template(html);
                    }
                },
                {
                    name: 'middle',
                    sizes: {
                        sm: '12'
                    },
                    className: 'panel-list-group'
                },
                {
                    name: 'bottom',
                    sizes: {
                        sm: '12'
                    },
                    className: 'panel-list-group'
                }
            ]
        ]
    });

    const InsightsSectionWidget = widgets.InsightsSectionWidget = RivalIQ.apps.Dashboard.models.Widget.mixin({
        mixins: {
            InsightsExportTitle: App.widgets.InsightsExportTitle
        },

        viewMore: false,
        scheduledExportAdvancedSettingsEnabled: false,

        getFilename: function () {
            const me = this;

            return RivalIQ.export.filename.base({
                title: _.result(me, 'title'),
                landscapeName: RivalIQ.state.view.get('landscapeName'),
                companyId: me.getProp('companyId')
            });
        },

        processProps: function (props) {
            const me = this;

            props.submetricDisplayType = 'upDownCarets';

            let interval = props.interval || 'daily';

            if (!me.isExporting()) {
                const intervalStr = RivalIQ.apps.Dashboard.views.IntervalDropdown.getGroupByInterval(props);
                interval = RivalIQ.apps.SocialPosts.utils.fixIntervalForMetric(intervalStr);
            }

            props.interval = interval;

            return props;
        },

        propsToSerializeForExport: []
    });

    function filterWidgets(widgets, props) {
        if (props.onlyInstanceId) {
            widgets = _.filter(widgets, function (widget) {
                return props.onlyInstanceId === widget.instanceId;
            });
        }

        if (props.onlyInstanceIds) {
            widgets = _.filter(widgets, function (widget) {
                return _.includes(props.onlyInstanceIds, widget.instanceId);
            });
        }

        return widgets;
    }

    const InsightsSectionWidgetWithInterval = InsightsSectionWidget.mixin({
        panelView: RivalIQ.apps.Dashboard.views.Panel.extend({
            showActions: function () {
                const me = this;

                me.actions.show(new RivalIQ.apps.Dashboard.views.IntervalDropdown({
                    dashboardModel: me.dashboardModel
                }), {
                    preventDestroy: true
                });

                me.showExportDropdown();
            }
        }),

        mixins: {
            userSettingsChanged: RivalIQ.apps.Dashboard.mixins.UserSettingsChanged
        },

        onUserSettingsChanged: function () {
            const me = this;

            const interval = RivalIQ.apps.Dashboard.views.IntervalDropdown.getGroupByInterval(me.getProps());
            me.setProps('interval', interval);
        },

        propsToSerializeForExport: ['interval']
    });

    const FacebookPageFansWidget = InsightsSectionWidgetWithInterval.mixin({
        title: RivalIQ.strings.facebook_insights_likes_widget_title,
        view: VerticalWithFlexLayoutSection,
        viewMore: false,

        description: RivalIQ.strings.facebook_insights_page_fans_widget_description,

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'likes-summary',
                    widgetId: widgetTypes.METRIC_SUMMARY,
                    props: {
                        trend: true,
                        group: 4,
                        metric: {
                            concept: Metrics.FACEBOOK_INSIGHTS_PAGE_FANS.id,
                            companyId: props.companyId
                        }
                    },
                    region: 'top'
                },
                {
                    instanceId: 'likes_stacked_bar',
                    widgetId: widgetTypes.LABELED_METRIC_LINE_CHART,
                    metricGroupType: 'likes',
                    metricLabelName: 'facebook_insights_profile_page_fans',
                    props: {
                        height: stackedBarChartHeight,
                        metrics: [{
                            name: 'Fans',
                            concept: Metrics.FACEBOOK_INSIGHTS_PAGE_FANS.id,
                            companyId: props.companyId,
                            dimensions: ['timeAnalysisMode', 'companyId', 'day', 'channel']
                        }]
                    },
                    region: 'middle'
                }
            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FB_INSIGHTS_PAGE_FANS, FacebookPageFansWidget);

    const LinkedInFollowersWidget = widgets.LinkedInFollowers = InsightsSectionWidgetWithInterval.extend({

        title: RivalIQ.strings.linked_in_insights_followers_title,
        description: RivalIQ.strings.linked_in_insights_followers_widget_description,

        view: VerticalSection,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['linkedin-total-followers', 'linkedin-followers']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'linkedin-follower-breakdown'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'linkedin-total-followers',
                    widgetId: widgetTypes.METRIC_SUMMARY,
                    props: {
                        inline: true,
                        trend: true,
                        metric: {
                            concept: Metrics.LINKED_IN_FOLLOWERS.id,
                            companyId: props.companyId
                        },
                        metrics: [
                            {
                                title: 'Net Followers, Paid',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_FOLLOWER_ADDS_PAID.id,
                                className: 'paid',
                                valueFormat: deltaValueFormatter
                            },
                            {
                                title: 'Net Followers, Organic',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_FOLLOWER_ADDS_ORGANIC.id,
                                className: 'organic',
                                valueFormat: deltaValueFormatter
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'linkedin-followers',
                    widgetId: widgetTypes.LABELED_METRIC_LINE_CHART,
                    metricGroupType: 'linkedin_insights_followers_by_day',
                    metricLabelName: 'linkedin_insights_profile_audience',
                    props: {
                        metrics: [
                            {
                                concept: Metrics.LINKED_IN_FOLLOWERS.id,
                                metricType: RivalIQ.enums.metricType.TOTAL,
                                companyId: props.companyId,
                                dimensions: ['timeAnalysisMode', 'companyId', 'day', 'channel']
                            }
                        ],
                        source: RivalIQ.enums.sources.LINKEDIN
                    },
                    region: 'middle'
                },
                {
                    instanceId: 'linkedin-follower-breakdown',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'linkedin_insights_net_followers_by_day',
                    metricLabelName: 'linkedin_insights_profile_audience_net_change',
                    props: {
                        metric: {
                            title: 'Followers',
                            companyId: props.companyId,
                            concept: Metrics.LINKED_IN_FOLLOWER_ADDS_ORGANIC.id,
                            valueFormat: deltaValueFormatter,
                            useIntervalDomainFormatting: true
                        },
                        metrics: [
                            {
                                title: 'Net Organic',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_FOLLOWER_ADDS_ORGANIC.id,
                                className: 'organic',
                                valueFormat: deltaValueFormatter,
                                useIntervalDomainFormatting: true
                            },
                            {
                                title: 'Net Paid',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_FOLLOWER_ADDS_PAID.id,
                                className: 'paid',
                                valueFormat: deltaValueFormatter
                            }
                        ]
                    },
                    region: 'bottom'
                }
            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.LI_INSIGHTS_FOLLOWERS, LinkedInFollowersWidget);

    const FacebookPageViewsWidget = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.facebook_insights_page_views_widget_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.facebook_insights_page_views_widget_description,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['page-views-summary', 'page-views-by-day-bar-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'page-engagements-by-day-bar-chart'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const height = (me.isExporting() ? stackedBarChartHeight : shortenedStackedBarChartHeight);

            const widgets = [
                {
                    instanceId: 'page-views-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            {
                                instanceId: 'page-views-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.FACEBOOK_INSIGHTS_PAGE_VIEWS_TOTAL.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'left'
                            },
                            {
                                instanceId: 'page-engagements-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.FACEBOOK_INSIGHTS_PAGE_TOTAL_ACTIONS.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'right'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'page-views-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'facebook_insights_profile_page_views_by_day',
                    metricLabelName: 'facebook_insights_profile_presence_page_views',
                    props: {
                        height: height,
                        metrics: [
                            {
                                concept: Metrics.FACEBOOK_INSIGHTS_PAGE_VIEWS_TOTAL.id,
                                companyId: props.companyId,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'middle'
                },
                {
                    instanceId: 'page-engagements-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_PROFILE_ENGAGEMENTS_STACKED_BAR_CHART,
                    metricGroupType: 'facebook_insights_profile_page_cta_by_day',
                    metricLabelName: 'facebook_insights_profile_presence_page_cta_clicks',
                    props: {
                        height: height,
                        metric: {
                            concept: Metrics.FACEBOOK_INSIGHTS_PAGE_TOTAL_ACTIONS.id,
                            companyId: props.companyId,
                            useIntervalDomainFormatting: true
                        },
                        metrics: [
                            {
                                title: 'CTA Clicks',
                                concept: Metrics.FACEBOOK_INSIGHTS_PAGE_CTA_CLICKS_LOGGED_IN_UNIQUE.id,
                                companyId: props.companyId,
                                className: 'legend-cta-clicks',
                                useIntervalDomainFormatting: true
                            },
                            {
                                title: 'Direction Clicks',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_PAGE_GET_DIRECTIONS_CLICKS_LOGGED_IN_UNIQUE.id,
                                className: 'legend-direction-clicks'
                            },
                            {
                                title: 'Phone Clicks',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_PAGE_CALL_PHONE_CLICKS_LOGGED_IN_UNIQUE.id,
                                className: 'legend-phone-clicks'
                            },
                            {
                                title: 'Website Clicks',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_PAGE_WEBSITE_CLICKS_LOGGED_IN_UNIQUE.id,
                                className: 'legend-website-clicks'
                            }
                        ]
                    },
                    region: 'bottom'
                }
            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FB_INSIGHTS_PAGE_VIEWS, FacebookPageViewsWidget);

    const FacebookPageReachGenderWidget = InsightsSectionWidget.extend({
        title: RivalIQ.strings.facebook_insights_page_reach_gender_widget_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.facebook_insights_page_reach_gender_widget_description,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'facebook-page-reach-age-gender'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'facebook-page-reach-age-gender',
                    widgetId: widgetTypes.INSIGHTS_DEMOGRAPHICS,
                    metricGroupType: 'page_reach_age_gender',
                    distributionClassName: 'fb-insights-age-gender',
                    props: {
                        height: 265,
                        demographicType: 'reach-by-age-gender',
                        companyId: props.companyId
                    },
                    region: 'middle'
                }
            ];
            return filterWidgets(widgets, props);
        }

    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FB_INSIGHTS_PAGE_REACH_GENDER, FacebookPageReachGenderWidget);

    const FacebookPageReachAgeWidget = InsightsSectionWidget.extend({
        title: RivalIQ.strings.facebook_insights_page_reach_age_widget_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.facebook_insights_page_reach_age_widget_description,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'reach-age-gender'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'reach-age-gender',
                    widgetId: widgetTypes.INSIGHTS_AGE_GENDER_DISTRIBUTION,
                    metricGroupType: 'page_reach_age_gender',
                    distributionClassName: 'fb-insights-age-gender',
                    props: {
                        companyId: props.companyId
                    },
                    region: 'middle'
                }
            ];
            return filterWidgets(widgets, props);
        }

    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FB_INSIGHTS_PAGE_REACH_AGE, FacebookPageReachAgeWidget);

    const FacebookPageConsumptionsWidget = widgets.FacebookPageConsumptionsWidget = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.facebook_insights_page_consumptions_widget_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.facebook_insights_page_consumptions_widget_description,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['page-consumptions-summary', 'page-engagements-by-day-bar-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'facebook-consumption-rate-by-day'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const height = (me.isExporting() ? stackedBarChartHeight : shortenedStackedBarChartHeight);

            const widgets = [
                {
                    instanceId: 'page-consumptions-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            {
                                instanceId: 'consumptions-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            abbreviatedName: 'Presence Consumptions',
                                            concept: Metrics.FACEBOOK_INSIGHTS_CONSUMPTIONS.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'left'
                            },
                            {
                                instanceId: 'consumptions-rate-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            abbreviatedName: 'Presence Consumption Rate by Imp.',
                                            name: 'Presence Consumption Rate by Impression',
                                            concept: Metrics.FACEBOOK_INSIGHTS_PAGE_ENGAGEMENT_RATE.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'right'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'page-engagements-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_PROFILE_ENGAGEMENTS_STACKED_BAR_CHART,
                    metricGroupType: 'facebook_insights_profile_page_consumptions_by_day',
                    metricLabelName: 'facebook_insights_profile_presence_consumptions',
                    props: {
                        height: height,
                        metric: {
                            title: 'Presence Consumptions',
                            concept: Metrics.FACEBOOK_INSIGHTS_CONSUMPTIONS.id,
                            companyId: props.companyId,
                            useIntervalDomainFormatting: true
                        },
                        metrics: [
                            {
                                title: 'Link Clicks',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_CONSUMPTIONS_LINK_CLICKS.id,
                                className: 'legend-link-clicks',
                                useIntervalDomainFormatting: true
                            },
                            {
                                title: 'Photo Views',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_CONSUMPTIONS_PHOTO_VIEWS.id,
                                className: 'legend-photo-views'
                            },
                            {
                                title: 'Video Plays',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_CONSUMPTIONS_VIDEO_PLAY.id,
                                className: 'legend-video-plays'
                            },
                            {
                                title: 'Other Clicks',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_CONSUMPTIONS_OTHER_CLICKS.id,
                                className: 'legend-other-clicks'
                            }
                        ]
                    },
                    region: 'middle'
                },
                {
                    instanceId: 'facebook-consumption-rate-by-day',
                    widgetId: widgetTypes.LABELED_METRIC_LINE_CHART,
                    metricGroupType: 'facebook_insights_profile_consumption_rate_by_day',
                    metricLabelName: 'facebook_insights_profile_presence_consumption_rate',
                    props: {
                        height: height,
                        metrics: [
                            {
                                title: 'Presence Consumption Rate by Impression',
                                concept: Metrics.FACEBOOK_INSIGHTS_PAGE_ENGAGEMENT_RATE.id,
                                companyId: props.companyId,
                                titleFormatter: _.identity,
                                dimensions: ['timeAnalysisMode', 'companyId', 'day', 'channel']
                            }
                        ]
                    },
                    region: 'bottom'
                }
            ];

            return filterWidgets(widgets, props);
        }

    });


    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FB_INSIGHTS_PAGE_CONSUMPTIONS, FacebookPageConsumptionsWidget);

    const FacebookImpressionsReachWidget = widgets.FacebookImpressionsReach = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.facebook_insights_impressions_reach_widget_title,
        view: VerticalWithFlexLayoutSection,

        description: function () {
            return App.metaDeprecatedMetricCards.cardDescriptionWithDeprecationNote(
                RivalIQ.strings.facebook_insights_impressions_reach_widget_description
            );
        },

        processProps: function (props, changed) {
            const me = this;

            props = InsightsSectionWidgetWithInterval.prototype.processProps.call(me, props, changed);
            App.metaDeprecatedMetricCards.resetWidgetCacheForTimePeriodChange(me, changed, props);

            return props;
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const isNewPageExperience = me.getCuration().presence.get('has_transitioned_to_new_page_experience');

            let widgets;
            if (isNewPageExperience || _.isUndefined(isNewPageExperience)) {
                widgets = [
                    {
                        instanceId: 'reach-summary',
                        widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                        props: {
                            summaryWidgets: [
                                {
                                    instanceId: 'presence-impressions',
                                    widgetId: widgetTypes.METRIC,
                                    props: {
                                        metrics: [
                                            {
                                                abbreviatedName: 'Presence Impressions',
                                                concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS.id
                                            }
                                        ],
                                        submetricOptions: {
                                            value: RivalIQ.enums.valueType.DELTA_REL
                                        },
                                        submetricType: 'delta_relative'
                                    },
                                    region: 'left'
                                },
                                {
                                    instanceId: 'presence-impressions-paid',
                                    widgetId: widgetTypes.METRIC,
                                    props: {
                                        metrics: [
                                            {
                                                abbreviatedName: 'Presence Impressions, Paid',
                                                concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS_PAID.id
                                            }
                                        ],
                                        submetricOptions: {
                                            value: RivalIQ.enums.valueType.DELTA_REL
                                        },
                                        submetricType: 'delta_relative'
                                    },
                                    region: 'right'
                                }
                            ]
                        },
                        region: 'top'
                    },
                    {
                        instanceId: 'reach-over-time',
                        widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                        metricGroupType: 'fb_reach',
                        metricLabelName: 'facebook_insights_profile_presence_impressions',
                        props: {
                            height: stackedBarChartHeight,
                            metrics: [
                                {
                                    title: 'Presence Impressions',
                                    companyId: props.companyId,
                                    concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS.id,
                                    useIntervalDomainFormatting: true
                                }
                            ]
                        },
                        region: 'middle'
                    }
                ];
            } else {
                widgets = [
                    {
                        instanceId: 'reach-summary',
                        widgetId: widgetTypes.METRIC_SUMMARY,
                        props: {
                            inline: true,
                            trend: true,
                            group: 3,
                            breakdown_header: 'Impressions By Source',
                            metric: {
                                title: 'Presence Impressions',
                                concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS.id,
                                companyId: props.companyId
                            },
                            metrics: [
                                {
                                    title: 'Paid',
                                    companyId: props.companyId,
                                    concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS_PAID.id,
                                    description: RivalIQ.strings.facebook_insights_impressions_paid_description,
                                    className: 'paid'
                                },
                                {
                                    title: 'Organic, Non-Viral',
                                    concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS_NONVIRAL.id,
                                    description: RivalIQ.strings.facebook_insights_impressions_nonviral_description,
                                    companyId: props.companyId,
                                    className: 'organic'
                                },
                                {
                                    title: 'Organic, Viral',
                                    companyId: props.companyId,
                                    concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS_VIRAL.id,
                                    description: RivalIQ.strings.facebook_insights_impressions_viral_description,
                                    className: 'viral'
                                }
                            ]
                        },
                        region: 'top'
                    },
                    {
                        instanceId: 'reach-over-time',
                        widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                        metricGroupType: 'fb_reach',
                        metricLabelName: 'facebook_insights_profile_presence_impressions',
                        props: {
                            height: stackedBarChartHeight,
                            companyId: props.companyId,
                            metric: {
                                title: 'Presence Impressions',
                                companyId: props.companyId,
                                concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS.id,
                                useIntervalDomainFormatting: true
                            },
                            metrics: [
                                {
                                    title: 'Paid',
                                    concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS_PAID.id,
                                    companyId: props.companyId,
                                    className: 'paid',
                                    useIntervalDomainFormatting: true
                                },
                                {
                                    title: 'Organic, Non-Viral',
                                    concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS_NONVIRAL.id,
                                    companyId: props.companyId,
                                    className: 'organic'
                                },
                                {
                                    title: 'Organic, Viral',
                                    concept: Metrics.FACEBOOK_INSIGHTS_IMPRESSIONS_VIRAL.id,
                                    companyId: props.companyId,
                                    className: 'viral'
                                }
                            ]
                        },
                        region: 'middle'
                    }
                ];
            }

            widgets = App.metaDeprecatedMetricCards.pruneFacebookImpressionsReachWidgets(widgets, me.getProp('timePeriod'));

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FB_INSIGHTS_IMPRESSIONS_REACH, FacebookImpressionsReachWidget);

    const LinkedInEngagementLineChartView = RivalIQ.apps.Dashboard.widgets.LineChart.extend({
        getTooltip: function () {
            const me = this;

            return {
                title: function (data, index) {
                    const model = me.collection.first();
                    return model.getDomainName(index);
                },

                content: function (data, idx) {
                    const mainMetric = me.tooltipMetrics.last();
                    const tooltipBreakdownMetrics = me.tooltipMetrics.slice(0, me.tooltipMetrics.length - 1);

                    const viewOptions = {
                        collection: new RivalIQ.collections.MetricDefinitions(_.map(tooltipBreakdownMetrics, function (metric, i) {
                            return new RivalIQ.models.MetricDefinition(_.extend(metric.toJSON(), {
                                value: metric.format(me.tooltipData[i][idx], {
                                    expand: true
                                })
                            }));
                        })),
                        model: new RivalIQ.models.MetricDefinition(_.extend(mainMetric.toJSON(), {
                            value: mainMetric.format(me.tooltipData[me.tooltipMetrics.length - 1][idx], {
                                expand: true
                            })
                        }))
                    };

                    return new RivalIQ.apps.Dashboard.widgets.MetricSummaryTooltip(viewOptions).render().el.outerHTML;
                }
            };
        },

        loadData: function () {

            const me = this;

            return RivalIQ.collections.MetricDefinitions.fetch({}, [undefined, me.dashboardModel.metrics])
                .done(function (metrics) {
                    me.model = metrics[1].last();
                    me.collection = new RivalIQ.collections.MetricDefinitions(me.model);
                    me.tooltipMetrics = metrics[1];
                    me.tooltipData = me.tooltipMetrics.invoke('getChartData');
                });
        }
    });

    const LinkedInEngagementLineChart = RivalIQ.apps.Dashboard.widgets.MetricLineChartWidget.extend({
        view: LinkedInEngagementLineChartView
    });
    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.LI_ENGAGEMENT_LINE_CHART, LinkedInEngagementLineChart);

    const GlanceMetricSummaryView = SectionLayout.extend({
        template: '#insightsSideBySideHorizontalExportTemplate',
        config: [
            [
                {
                    name: 'left',
                    sizes: {
                        sm: '6'
                    },
                    className: 'panel-list-group'
                },
                {
                    name: 'right',
                    sizes: {
                        sm: '6'
                    },
                    className: 'panel-list-group'
                }
            ]
        ]
    });

    const GlanceMetricSummaryWidget = RivalIQ.apps.Dashboard.models.Widget.mixin({
        view: GlanceMetricSummaryView,

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();
            return props.summaryWidgets;
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.GLANCE_METRIC_SUMMARY, GlanceMetricSummaryWidget);


    const LabeledLineChartView = RivalIQ.apps.Dashboard.widgets.LineChart.extend({
        template: '#labeledLineChartWidgetTemplate',

        ui: {
            metricLabel: '.metric-label'
        },

        getTooltip: function () {
            const me = this;

            return {
                title: function (data, index) {
                    const model = me.collection.first();
                    return model.getDomainName(index);
                },

                content: function (data) {
                    const viewOptions = {
                        collection: new RivalIQ.collections.MetricDefinitions(me.collection.map(function (metric, i) {
                            return new RivalIQ.models.MetricDefinition(_.extend(metric.toJSON(), {
                                value: metric.format(data[0].value[i], {
                                    expand: true
                                })
                            }));
                        }))
                    };

                    return new RivalIQ.apps.Dashboard.widgets.MetricSummaryTooltip(viewOptions).render().el.outerHTML;
                }
            };
        },

        serializeData: function () {
            const me = this;
            return {
                metricLabel: _.result(me.dashboardModel, 'getMetricLabel')
            };
        },

        reload: function () {
            const me = this;
            if (me.ui.metricLabel && me.ui.metricLabel.html) {
                const metricLabel = _.result(me.dashboardModel, 'getMetricLabel');
                me.ui.metricLabel.html(metricLabel);
            }

            RivalIQ.apps.Dashboard.widgets.LineChart.prototype.reload.apply(me, arguments);
        }
    });

    const LabeledLineChart = RivalIQ.apps.Dashboard.widgets.MetricLineChartWidget.extend({
        view: LabeledLineChartView,

        processProps: function (props) {
            const me = this;

            let interval = props.interval || 'day';
            if (!me.isExporting()) {
                interval = RivalIQ.apps.Dashboard.views.IntervalDropdown.getGroupByInterval(props);
            }
            if (RivalIQ.enums.timePeriod.interval[interval]) {
                interval = RivalIQ.enums.timePeriod.interval[interval];
            }

            props.metrics[0] = RivalIQ.metric.timePeriod.setInterval(props.metrics[0], interval);
            props.metrics[0].useIntervalDomainFormatting = true;

            // We need to delete me.metrics here otherwise Chart.buildMetrics will noop when we update the metric
            // (e.g. on interval change).
            delete me.metrics;
            props = RivalIQ.apps.Dashboard.widgets.Chart.buildMetrics(me, props);

            return props;
        },

        getMetricLabel: function () {
            const me = this;

            const interval = (me.getProp('interval') || me.getProp('defaultInterval'));
            const intervalStr = RivalIQ.enums.timePeriod.interval[interval] || RivalIQ.enums.timePeriod.interval.daily;
            return RivalIQ.str(me.metricLabelName, intervalStr);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.LABELED_METRIC_LINE_CHART, LabeledLineChart);

    const StackedBarWithLegend = RivalIQ.apps.Dashboard.widgets.StackedBar.extend({
        template: '#insightsProfileEngagementsTemplate',

        ui: {
            metricLabel: '.metric-label',
            legendItem: '.profile-eng-legend-item',
            noDataMsgContainer: '.insights-no-data-msg-container',
            noDataMsg: '.insights-no-data-msg',
            dashboardRegions: '.dashboard-regions'
        },

        events: {
            'click @ui.legendItem': 'onLegendItemClick'
        },

        serializeData: function () {
            const me = this;
            return {
                metricLabel: _.result(me.dashboardModel, 'getMetricLabel'),
                legendItems: me.getMetricLegendItems()
            };
        },

        loadData: function () {
            const me = this;

            return RivalIQ.apps.Dashboard.widgets.StackedBar.prototype.loadData.apply(me)
                .then(function () {
                    me._unredactedCollection = me.collection.clone();
                    me._resetRedactedMetrics();
                });
        },

        getMetricLegendItems: function () {
            const me = this;

            const exclusions = me.getProp('exclusions') || {};

            const legendItems = _.map(me._unredactedCollection.models, function (metric, idx) {
                return {
                    name: metric.getName(),
                    class: metric.getClassName(),
                    disabled: exclusions[idx]
                };
            });
            return legendItems;
        },

        _resetRedactedMetrics: function () {
            const me = this;

            const exclusions = me.getProp('exclusions') || {};

            const toRemove = _.reduce(exclusions, function (memo, shouldExclude, idx) {
                if (shouldExclude) {
                    memo.push(me._unredactedCollection.at(idx));
                }

                return memo;
            }, []);

            me.collection = me._unredactedCollection.clone();
            if (!_.isEmpty(toRemove)) {
                me.collection.remove(toRemove);
            }
        },

        onLegendItemClick: function (e) {
            const me = this;

            let legendItem = $(e.target);
            let idx = legendItem.data('idx');

            // if they clicked on the color pill, we need to move up to the parent element.
            if (_.isUndefined(idx)) {
                legendItem = legendItem.parent();
                idx = legendItem.data('idx');
            }

            if (_.isUndefined(idx)) {
                return;
            }

            const exclusions = me.getProp('exclusions') || {};
            exclusions[idx] = !exclusions[idx];

            // silent as we're going to re-render the chart anyway after resetting the redacted metrics.
            me.setProps('exclusions', exclusions, {
                silent: true
            });
            me._resetRedactedMetrics();
            me.showChart();

            if (exclusions[idx]) {
                legendItem.addClass('x-legend-excluded');
            } else {
                legendItem.removeClass('x-legend-excluded');
            }
        }
    });

    const StackedBarWithLegendWidget = RivalIQ.apps.Dashboard.widgets.MetricStackedBarChartWidget.extend({
        view: StackedBarWithLegend
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_PROFILE_ENGAGEMENTS_STACKED_BAR_CHART, StackedBarWithLegendWidget);

    const InstagramProfileAnalytics = widgets.InstagramProfileAnalytics = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.instagram_insights_profile_analytics_widget_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.instagram_insights_profile_analytics_widget_description,

        getPropsForFormat: function (format) {
            const me = this;

            const props = InsightsSectionWidget.prototype.getPropsForFormat.call(me, format);

            // grab the exclusions from the bar chart so they're reflected on export.
            const barChart = me.getWidget('profile-engagements-bar-chart');
            if (barChart) {
                props.exclusions = barChart.getProp('exclusions');
            }

            return props;
        },

        getSlides: function (overrides) {
            const me = this;

            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['profile-summary', 'profile-views-bar-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'profile-engagements-bar-chart'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'profile-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            {
                                instanceId: 'profile-views-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.INSTAGRAM_INSIGHTS_PROFILE_VIEWS.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'left'
                            },
                            {
                                instanceId: 'profile-eng-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.INSTAGRAM_INSIGHTS_PROFILE_ENGAGEMENTS.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'right'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'profile-views-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'profile_views',
                    metricLabelName: 'instagram_insights_profile_presence_profile_views',
                    props: {
                        height: stackedBarChartHeight,
                        metrics: [
                            {
                                name: 'Profile Views, Unique',
                                concept: Metrics.INSTAGRAM_INSIGHTS_PROFILE_VIEWS.id,
                                companyId: props.companyId,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'middle'
                },
                {
                    instanceId: 'profile-engagements-bar-chart',
                    widgetId: widgetTypes.METRIC_PROFILE_ENGAGEMENTS_STACKED_BAR_CHART,
                    metricGroupType: 'profile_eng',
                    metricLabelName: 'instagram_insights_profile_presence_profile_engagements',
                    props: {
                        height: stackedBarChartHeight,
                        metric: {
                            title: 'Profile Engagements',
                            concept: Metrics.INSTAGRAM_INSIGHTS_PROFILE_ENGAGEMENTS.id,
                            companyId: props.companyId,
                            useIntervalDomainFormatting: true
                        },
                        metrics: [
                            {
                                title: 'Website Clicks',
                                companyId: props.companyId,
                                concept: Metrics.INSTAGRAM_INSIGHTS_WEBSITE_CLICKS.id,
                                className: 'legend-instagram-profile-engagements-website-clicks',
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'bottom'
                }
            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_PROFILE_ANALYTICS, InstagramProfileAnalytics);

    const LinkedInProfileAnalytics = widgets.LinkedInProfileAnalytics = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.linked_in_insights_profile_analytics_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.linked_in_insights_profile_analytics_widget_description,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['profile-summary', 'profile-view-by-day-bar-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'unique-profile-view-by-day-bar-chart'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'profile-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            {
                                instanceId: 'profile-views-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.LINKED_IN_OVERVIEW_PAGE_VIEWS.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'left'
                            },
                            {
                                instanceId: 'unique-profile-view-by-day-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.LINKED_IN_AVERAGE_OVERVIEW_PAGE_UNIQUE_VIEWS.id,
                                            valueFormat: valueFormatter
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'right'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'profile-view-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'linkedin_insights_profile_analytics_profile_views_by_day',
                    metricLabelName: 'linkedin_insights_profile_page_views',
                    props: {
                        height: stackedBarChartHeight,
                        metrics: [
                            {
                                concept: Metrics.LINKED_IN_OVERVIEW_PAGE_VIEWS.id,
                                companyId: props.companyId,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'middle'
                },
                {
                    instanceId: 'unique-profile-view-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'linkedin_insights_profile_analytics_unique_profile_views_by_day',
                    metricLabelName: () => {
                        return (me.getProp('interval') === 'daily' ?
                            'linkedin_insights_profile_unique_page_views_per_day' :
                            'linkedin_insights_profile_unique_page_views');
                    },
                    props: {
                        height: stackedBarChartHeight,
                        metrics: [
                            {
                                concept: Metrics.LINKED_IN_OVERVIEW_PAGE_UNIQUE_VIEWS.id,
                                companyId: props.companyId,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'bottom'
                }

            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.LI_INSIGHTS_PROFILE_ANALYTICS, LinkedInProfileAnalytics);


    const LinkedInPostImpressionsPerReach = widgets.LinkedInPostImpressionsPerReach = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.linked_in_insights_post_impressions_per_reach_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.linked_in_insights_post_impressions_per_reach_widget_description,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['impressions-reach-summary', 'post-impressions-by-day-bar-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'post-reach-by-day-bar-chart'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'impressions-reach-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            {
                                instanceId: 'impressions-per-day-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.LINKED_IN_UPDATE_IMPRESSIONS.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'left'
                            },
                            {
                                instanceId: 'reach-per-day-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.LINKED_IN_AVERAGE_VISITORS_UNIQUE.id,
                                            abbreviatedName: 'Presence Update Reach per Day',
                                            valueFormat: valueFormatter
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'right'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'post-impressions-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'linkedin_insights_profile_post_impressions_by_day',
                    metricLabelName: 'linkedin_insights_profile_update_impressions',
                    props: {
                        metrics: [
                            {
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_IMPRESSIONS.id,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'middle'
                },
                {
                    instanceId: 'post-reach-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'linkedin_insights_profile_post_reach_by_day',
                    metricLabelName: () => {
                        return (me.getProp('interval') === 'daily' ?
                            'linkedin_insights_profile_update_reach_per_day' :
                            'linkedin_insights_profile_update_reach');
                    },
                    props: {
                        metrics: [
                            {
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_VISITORS_UNIQUE.id,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'bottom'
                }
            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.LI_INSIGHTS_POST_IMPRESSIONS_PER_REACH, LinkedInPostImpressionsPerReach);


    const LinkedInPostEngagements = widgets.LinkedInPostEngagements = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.linked_in_insights_post_engagements_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.linked_in_insights_post_engagements_widget_description,

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['post-engagements-summary', 'post-engagement-by-day-bar-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'post-engagement-rate-by-day-line-chart'
                }, overrides))
            ];
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'post-engagements-summary',
                    widgetId: widgetTypes.METRIC_SUMMARY,
                    props: {
                        inline: true,
                        trend: true,
                        metric: {
                            concept: Metrics.LINKED_IN_ENGAGEMENT.id,
                            companyId: props.companyId
                        },
                        metrics: [
                            {
                                title: 'Clicks',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_CLICKS.id,
                                className: 'legend-linkedin-post-engagements-clicks'
                            },
                            {
                                title: 'Likes',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_LIKES.id,
                                className: 'legend-linkedin-post-engagements-likes'
                            },
                            {
                                title: 'Comments',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_COMMENTS.id,
                                className: 'legend-linkedin-post-engagements-comments'
                            },
                            {
                                title: 'Shares',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_SHARES.id,
                                className: 'legend-linkedin-post-engagements-shares'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'post-engagement-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'linkedin_insights_profile_post_engagement_by_day',
                    metricLabelName: 'linkedin_insights_profile_update_engagement',
                    props: {
                        height: stackedBarChartHeight,
                        metric: {
                            title: 'Presence Update Engagement Total',
                            concept: Metrics.LINKED_IN_ENGAGEMENT.id,
                            companyId: props.companyId,
                            useIntervalDomainFormatting: true

                        },
                        metrics: [
                            {
                                title: 'Clicks',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_CLICKS.id,
                                className: 'legend-linkedin-post-engagements-clicks',
                                useIntervalDomainFormatting: true
                            },
                            {
                                title: 'Likes',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_LIKES.id,
                                className: 'legend-linkedin-post-engagements-likes'
                            },
                            {
                                title: 'Comments',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_COMMENTS.id,
                                className: 'legend-linkedin-post-engagements-comments'
                            },
                            {
                                title: 'Shares',
                                companyId: props.companyId,
                                concept: Metrics.LINKED_IN_UPDATE_SHARES.id,
                                className: 'legend-linkedin-post-engagements-shares'
                            }
                        ]
                    },
                    region: 'middle'
                },
                {
                    instanceId: 'post-engagement-rate-by-day-line-chart',
                    widgetId: widgetTypes.LABELED_METRIC_LINE_CHART,
                    metricGroupType: 'linkedin_insights_profile_post_engagement_rate_by_day',
                    metricLabelName: 'linkedin_insights_profile_update_engagement_rate',
                    props: {
                        metrics: [
                            {
                                concept: Metrics.LINKED_IN_ENGAGEMENT_RATE.id,
                                companyId: props.companyId,
                                name: 'Presence Update Engagement Rate',
                                dimensions: ['timeAnalysisMode', 'companyId', 'day', 'channel']
                            }
                        ]
                    },
                    region: 'bottom'
                }
            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.LI_INSIGHTS_POST_ENGAGEMENTS, LinkedInPostEngagements);

    const VerticalNormalizeHeights = VerticalSection.extend({
        template: '#insightsVerticalNormalizeHeightsTemplate'
    });

    const MetricChanges = widgets.MetricChanges = InsightsSectionWidgetWithInterval.extend({
        view: VerticalNormalizeHeights,
        viewMore: false,

        getTitle: function () {
            const me = this;

            if (me.isExportFocus() && me.isExporting()) {
                return me.getExportTitle();
            }

            const title = me.getProp('title');
            if (title) {
                return title;
            }

            const metric = _.head(me.getProp('metrics'));
            const nameDimensions = me.getProp('nameDimensions');

            return RivalIQ.metric.names.metricName(metric, {
                dimensions: nameDimensions
            });
        },

        getPropsForFormat: function (format) {
            const me = this;

            const props = InsightsSectionWidgetWithInterval.prototype.getPropsForFormat.call(me, format);

            // grab the exclusions from the bar chart so they're reflected on export.
            const barChart = me.getWidget('metric-bar-chart');
            if (barChart) {
                props.exclusions = barChart.getProp('exclusions');
            }

            return props;
        },

        getSlides: function (overrides) {
            const me = this;

            return [
                me.buildSlide(overrides)
            ];
        },

        getMetricWidgetOptions: _.noop,
        getMetricWidget: function () {
            const me = this;
            const props = me.getProps();
            const metric = _.head(props.metrics);

            const metricOptions = _.defaults({}, me.getMetricWidgetOptions());
            const totalMetric = RivalIQ.metric.build(metric, metricOptions);

            return {
                instanceId: 'metric',
                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                props: {
                    metrics: [totalMetric],
                    submetricOptions: {
                        value: RivalIQ.enums.valueType.DELTA_REL
                    },
                    submetricType: 'delta_relative'
                }
            };
        },

        getMetricChangeWidgetOptions: _.noop,

        getMetricChangeWidget: function () {
            const me = this;
            const props = me.getProps();

            const metric = _.head(props.metrics);
            const metricOptions = _.defaults({
                timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE,
                valueFormat: function (value) {
                    return RivalIQ.formatter.changeSignPrefix(value) + RivalIQ.formatter.formatAbbreviated(value);
                }
            }, me.getMetricChangeWidgetOptions());
            const deltaAbsoluteMetric = RivalIQ.metric.build(metric, metricOptions);

            return {
                instanceId: 'change-metric',
                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                props: {
                    metrics: [deltaAbsoluteMetric],
                    nameDimensions: ['timeAnalysisMode'],
                    metricOptions: {
                        value: RivalIQ.enums.valueType.DELTA
                    }
                },
                region: 'right'
            };
        },

        getMetricLineWidget: function () {
            const me = this;
            const props = me.getProps();

            const metric = _.head(props.metrics);
            const dailyValuesMetric = RivalIQ.metric.build(metric, {
                companyId: props.companyId,
                dimensions: ['timeAnalysisMode', 'companyId', 'day', 'channel']
            });

            return {
                widgetId: widgetTypes.LABELED_METRIC_LINE_CHART,
                props: {
                    metrics: [dailyValuesMetric],
                    height: 157,
                    labels: {}
                }
            };
        },

        getMetricBarWidgetOptions: _.noop,
        getMetricBarChangeWidgetOptions: _.noop,

        getMetricBarWidget: function () {
            const me = this;
            const props = me.getProps();

            const metric = _.head(props.metrics);
            const metricOptions = _.defaults({
                companyId: props.companyId,
                dimensions: ['day', 'timeAnalysisMode', 'companyId', 'channel'],
                timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE,
                useIntervalDomainFormatting: true
            }, me.getMetricBarWidgetOptions());

            const barMetric = RivalIQ.metric.build(metric, metricOptions);
            const barChangeMetric = RivalIQ.metric.build(metric, _.defaults({
                companyId: props.companyId,
                dimensions: ['day', 'timeAnalysisMode', 'companyId', 'channel'],
                timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE,
                useIntervalDomainFormatting: true
            }, me.getMetricBarChangeWidgetOptions()));

            const barMetrics = [
                _.defaults(barChangeMetric, {
                    title: RivalIQ.metric.names.metricName(barChangeMetric, {
                        dimensions: ['timeAnalysisMode']
                    }),
                    valueFormat: function (value) {
                        return RivalIQ.formatter.changeSignPrefix(value) + RivalIQ.formatter.formatAbbreviated(value);
                    }
                })
            ];

            return {
                widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                props: {
                    showPlusMinusGraph: true,
                    metric: barMetric,
                    metrics: barMetrics,
                    height: 157,
                    labels: {},
                    updateMetricsWithInterval: true
                }
            };
        },

        widgetDefinitions: function () {
            const me = this;
            return filterWidgets([
                {
                    instanceId: 'metric-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            _.defaults({
                                region: 'left'
                            }, me.getMetricWidget()),
                            _.defaults({
                                region: 'right'
                            }, me.getMetricChangeWidget())
                        ]
                    },
                    region: 'top'
                },
                _.defaults({
                    instanceId: 'metric-line-chart',
                    region: 'middle'
                }, me.getMetricLineWidget()),
                _.defaults({
                    instanceId: 'metric-bar-chart',
                    region: 'bottom'
                }, me.getMetricBarWidget())
            ], me.getProps());
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_CHANGES, MetricChanges);

    // Note: I'm not using the default views from this file here as they are incompatible with universalMetricsApi.
    const TwitterFollowersView = RivalIQ.views.LayoutView.mixin({

        template: '#twitterInsightsFollowersTemplate',

        ui: {
            metricPanelContainer: '.metric-panel-container',
            metricLineChartContainer: '.metric-line-chart-container',
            metricBarChartContainer: '.metric-bar-chart-container'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            normalizeHeights: RivalIQ.apps.Dashboard.mixins.NormalizeHeights
        },

        regions: {
            metricLeftRegion: '.metric-left-region',
            metricRightRegion: '.metric-right-region',
            metricLineChartRegion: '.metric-line-chart',
            metricBarChartRegion: '.metric-bar-chart'
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            let widget = me.dashboardModel.getWidget('metric-summary-left');
            if (widget) {
                me.metricLeftRegion.showWidget(widget);
            }
            widget = me.dashboardModel.getWidget('metric-summary-right');
            if (widget) {
                me.metricRightRegion.showWidget(widget);
            }
            widget = me.dashboardModel.getWidget('metric-line-chart');
            if (widget) {
                me.metricLineChartRegion.showWidget(widget);
            }
            widget = me.dashboardModel.getWidget('metric-bar-chart');
            if (widget) {
                me.metricBarChartRegion.showWidget(widget);
            }
        },

        onRenderLoaded: function () {
            const me = this;

            let widget = me.dashboardModel.getWidget('metric-summary-right');
            if (!widget) {
                me.ui.metricPanelContainer.remove();
            }
            widget = me.dashboardModel.getWidget('metric-line-chart');
            if (!widget) {
                me.ui.metricLineChartContainer.remove();
            }
            widget = me.dashboardModel.getWidget('metric-bar-chart');
            if (!widget) {
                me.ui.metricBarChartContainer.remove();
            }
        }
    });

    const TwitterFollowers = widgets.TwitterFollowers = MetricChanges.extend({
        view: TwitterFollowersView,
        description: RivalIQ.strings.twitter_insights_followers_widget_description,
        // NOTE we want to override the default behavior that is meant to
        // accommodate the generalized/automated, Custom Dashboards experience
        getMetricWidgetOptions: function () {
            return {
                name: 'Followers',
                abbreviatedName: 'Followers'
            };
        },
        getMetricChangeWidgetOptions: function () {
            return {
                name: 'Followers Net Change',
                abbreviatedName: 'Followers Net Change',
                description: RivalIQ.strings.twitter_insights_followers_count_change_profile_description
            };
        },
        getMetricBarWidgetOptions: function () {
            return {
                name: 'Followers Net Change By Day'
            };
        },
        getMetricBarChangeWidgetOptions: function () {
            return {
                title: 'Followers Net Change'
            };
        },

        getMetricLineWidget: function () {
            const me = this;

            const widget = MetricChanges.prototype.getMetricLineWidget.call(me);
            widget.props.metrics[0].name = 'Followers';
            return widget;
        },

        widgetDefinitions: function () {
            const me = this;
            return filterWidgets([
                _.defaults({
                    instanceId: 'metric-summary-left'
                }, me.getMetricWidget()),
                _.defaults({
                    instanceId: 'metric-summary-right'
                }, me.getMetricChangeWidget()),
                _.defaults({
                    instanceId: 'metric-line-chart',
                    metricLabelName: 'twitter_insights_profile_audience'
                }, me.getMetricLineWidget()),
                _.defaults({
                    instanceId: 'metric-bar-chart',
                    metricLabelName: 'twitter_insights_profile_audience_net_change'
                }, me.getMetricBarWidget())
            ], me.getProps());
        },

        getSlides: function (overrides) {
            const me = this;
            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['metric-summary-left', 'metric-summary-right', 'metric-line-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'metric-bar-chart'
                }, overrides))
            ];
        },

        getProps: function () {
            const me = this;

            const baseProps = MetricChanges.prototype.getProps.call(me);
            // Though this is twitter insights, we're showing public twitter metrics here.
            baseProps.source = RivalIQ.enums.sources.TWITTER;
            return baseProps;
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.TWITTER_INSIGHTS_FOLLOWERS, TwitterFollowers);

    const InstagramFollowers = widgets.InstagramFollowers = MetricChanges.extend({
        title: RivalIQ.strings.instagram_insights_audience_gender_age_name,
        description: RivalIQ.strings.instagram_insights_followers_widget_description,
        // NOTE we want to override the default behavior that is meant to
        // accommodate the generalized/automated, Custom Dashboards experience
        getMetricChangeWidgetOptions: function () {
            return {
                name: 'Followers Net Change',
                description: RivalIQ.strings.instagram_insights_followers_count_change_profile_description
            };
        },
        getMetricLineWidget: function () {
            const me = this;
            const props = me.getProps();

            const metric = _.head(props.metrics);
            const dailyValuesMetric = RivalIQ.metric.build(metric, {
                companyId: props.companyId,
                dimensions: ['timeAnalysisMode', 'companyId', 'day', 'channel']
            });

            return {
                widgetId: widgetTypes.LABELED_METRIC_LINE_CHART,
                metricGroupType: 'instagram_insights_followers_by_day',
                metricLabelName: 'instagram_insights_profile_presence_audience',
                props: {
                    metrics: [dailyValuesMetric],
                    height: stackedBarChartHeight,
                    labels: {}
                }
            };
        },
        getMetricBarWidget: function () {
            const me = this;
            const props = me.getProps();
            return {
                widgetId: widgetTypes.METRIC_PROFILE_ENGAGEMENTS_STACKED_BAR_CHART,
                metricGroupType: 'insta_new_followers',
                metricLabelName: 'instagram_insights_profile_presence_net_change',
                props: {
                    metrics: [
                        {
                            title: 'Follower Adds',
                            companyId: props.companyId,
                            concept: Metrics.INSTAGRAM_INSIGHTS_NEW_FOLLOWER_COUNT.id,
                            className: 'insta-new-followers',
                            useIntervalDomainFormatting: true
                        }
                    ]
                }
            };
        },

        getSlides: function (overrides) {
            const me = this;

            return [
                me.buildSlide(_.defaults({
                    onlyInstanceIds: ['metric-summary', 'metric-line-chart']
                }, overrides)),
                me.buildSlide(_.defaults({
                    onlyInstanceId: 'metric-bar-chart'
                }, overrides))
            ];
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_FOLLOWERS, InstagramFollowers);

    const InstagramReach = widgets.InstagramReach = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.instagram_insights_reach_widget_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.instagram_insights_reach_widget_description,

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'profile-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            {
                                instanceId: 'profile-reach-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            name: 'Presence Reach per Day',
                                            concept: Metrics.INSTAGRAM_INSIGHTS_REACH_PER_DAY.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'left'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'reach-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'reach-by-day',
                    metricLabelName: () => {
                        return (me.getProp('interval') === 'daily' ?
                            'instagram_insights_profile_presence_presence_reach_per_day' :
                            'instagram_insights_profile_presence_presence_reach');
                    },
                    props: {
                        height: stackedBarChartHeight,
                        metrics: [
                            {
                                concept: Metrics.INSTAGRAM_INSIGHTS_REACH_PER_DAY.id,
                                companyId: props.companyId,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'middle'
                }
            ];
            return filterWidgets(widgets, props);

        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_REACH, InstagramReach);

    const InstagramImpressions = widgets.InstagramImpressions = InsightsSectionWidgetWithInterval.extend({
        title: RivalIQ.strings.instagram_insights_impressions_widget_title,
        view: VerticalSection,
        viewMore: false,
        description: RivalIQ.strings.instagram_insights_impressions_widget_description,

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'presence-impressions-summary',
                    widgetId: widgetTypes.GLANCE_METRIC_SUMMARY,
                    props: {
                        summaryWidgets: [
                            {
                                instanceId: 'profile-impressions-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            concept: Metrics.INSTAGRAM_INSIGHTS_IMPRESSIONS.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'left'
                            },
                            {
                                instanceId: 'profile-impressions-per-day-metric',
                                widgetId: RivalIQ.enums.widgetTypes.METRIC,
                                props: {
                                    metrics: [
                                        {
                                            name: 'Presence Impressions per Day',
                                            concept: Metrics.INSTAGRAM_INSIGHTS_IMPRESSIONS_PER_DAY.id
                                        }
                                    ],
                                    submetricOptions: {
                                        value: RivalIQ.enums.valueType.DELTA_REL
                                    },
                                    submetricType: 'delta_relative'
                                },
                                region: 'right'
                            }
                        ]
                    },
                    region: 'top'
                },
                {
                    instanceId: 'impressions-by-day-bar-chart',
                    widgetId: widgetTypes.METRIC_STACKED_BAR_CHART,
                    metricGroupType: 'impressions-by-day',
                    metricLabelName: 'instagram_insights_profile_presence_presence_impressions',
                    props: {
                        height: stackedBarChartHeight,
                        metrics: [
                            {
                                concept: Metrics.INSTAGRAM_INSIGHTS_IMPRESSIONS_PER_DAY.id,
                                companyId: props.companyId,
                                useIntervalDomainFormatting: true
                            }
                        ]
                    },
                    region: 'middle'
                }
            ];
            return filterWidgets(widgets, props);

        }

    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_IMPRESSIONS, InstagramImpressions);

    const InstagramFollowerOnlineTimesWidget = widgets.InstagramFollowerOnlineTimes = InsightsSectionWidget.extend({
        title: RivalIQ.strings.instagram_insights_follower_online_times_widget_title,
        view: App.views.InstagramFollowerOnlineTimesLayout,
        viewMore: false,
        description: RivalIQ.strings.instagram_insights_follower_online_times_widget_description,

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const widgets = [
                {
                    instanceId: 'insta-online-times',
                    className: 'panel-body',
                    widgetId: widgetTypes.FOLLOWER_ONLINE_TIMES,
                    props: {
                        companyId: props.companyId
                    }
                }
            ];

            return filterWidgets(widgets, props);
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_FOLLOWER_ONLINE_TIMES, InstagramFollowerOnlineTimesWidget);

}, window.d3);
